/**
 * The cover designs of the automatic album (ChronologicalCreator), and the rule choosing one. Data
 * only : DesignedCover draws them.
 *
 * A design is chosen for a product without collection covers whose album category was recognised
 * (options.category, the TitleSuggester classification, see config/albumtitles.php) :
 *  - a trip of a few days in one of the cities of `cities` : that city's design
 *  - another trip in one country of `countries` : that country's design
 *  - otherwise the design of the category, `categories`
 *
 * Every length is a fraction of the page, so the design follows the shape of the book ; font sizes
 * are page units (millimetres), as everywhere else in a creation, and a title too long for its block
 * is reduced by ChronologicalCreator._fitOneLine.
 *
 *  background   the colour of the whole cover, front, back and slice
 *  photo        { x, y, width, height } of the curated photo ; `square` makes it as tall as it is
 *               wide (the round designs), `mask` is a photo_masks id (26 : the circle) or the file
 *               of one ("arche.png"), see DesignedCover._maskId ; `ratio` (height / width) keeps the
 *               photo at those proportions within `maxWidth`, see DesignedCover._photoRect
 *  backPhoto    a second photo on the back cover, same { x, y, width, height }
 *  frame        a plain rectangle drawn under the photo : the polaroid card, the white border
 *  stickers     [{ id, x, y, width, height, right, bottom, belowPhoto }] theme_sticker ids laid over
 *               the photo. `width` and `height` are fractions of the page *width*, so that a sticker
 *               keeps its shape on any book ; `right` / `bottom` (instead of `x` / `y`) measure from
 *               the right and bottom edges, so that it stays in its corner ; `belowPhoto` (instead
 *               of `y`, a fraction of the page width too) puts its bottom that far under the bottom
 *               of the photo, so that it overlaps the photo the same way whatever the book
 *  title        { font, size, color, y } and, when the design asks for it, `bold`, `italic`, `upper`,
 *               `align` and the { x, width } of its block (the printable width by default). `y` is
 *               the top of the text block, the stage adding its own margin.
 *  subtitle     the same, for the line under the title (the category subtitle of the TitleSuggester,
 *               else the period of the photos) ; `prefix` opens that line, `spaced` spreads its
 *               letters (thin spaces : the stage and the print know no letter-spacing), `fixed`
 *               keeps it at its `y` even when the title above would reach it
 *  heading      a fixed line written as it is, `text` in the style of `title` (the name of the city
 *               of a city break, the album title going elsewhere)
 *  coverSubject the cover needs a precise picture (the bride and groom of a wedding) : Claude picks it
 *               among the best candidates, see ChronologicalCreator._chooseCoverPhoto
 */
fx.ns("covers", class CoverDesigns {

    /** The design for the album, null when none applies. `photos` : every photo given to the creator. */
    static choose(options, photos)
    {
        // ?cover-city=barcelone on the editor url : that city's cover whatever the photos, to try
        // each design without photos taken there
        let forced = CoverDesigns.forcedCity();
        if (forced)
            return forced.design;

        let category = options ? options.category : "";

        if (!category)
            return null;

        if (category == "travel")
            return CoverDesigns.cityDesign(photos) || CoverDesigns.countryDesign(options.country) || CoverDesigns.categories.travel;

        return CoverDesigns.categories[category] || null;
    }

    // ----------------------------- categories ------------------------------ //

    static get categories()
    {
        return {
            // a trip : the photo bleeds over the top of the cover, the title on the dark band below.
            // A trip in one country of `countries` gets that country's design instead
            travel : {
                background : "rgb(75, 73, 69)",
                photo : { x : 0, y : 0, width : 1, height : 0.69 },
                title : { font : "Open Sans", bold : true, size : 18, color : "rgb(255, 255, 255)", y : 0.728 },
                subtitle : { font : "Open Sans", upper : true, spaced : true, size : 4.6, color : "rgb(255, 255, 255)", y : 0.858 }
            },

            // a wedding : a portrait photo on sand, the names in a script. An album without the
            // bride and groom on its cover is a miss : the picture is chosen by Claude
            wedding : {
                background : "rgb(235, 219, 199)",
                coverSubject : true,
                photo : { x : 0.238, y : 0, width : 0.526, height : 0.745 },
                title : { font : "Great Vibes", size : 17, color : "rgb(196, 150, 55)", y : 0.774 },
                subtitle : { font : "Lora", size : 5.4, color : "rgb(55, 55, 54)", y : 0.861 }
            },

            // a birth : the photo framed in white on a soft green, the newborn on it
            baby : {
                background : "rgb(217, 225, 211)",
                coverSubject : true,
                frame : { color : "rgb(255, 255, 255)", x : 0.158, y : 0.107, width : 0.685, height : 0.670 },
                photo : { x : 0.176, y : 0.125, width : 0.649, height : 0.634 },
                title : { font : "Niconne", size : 11.4, color : "rgb(97, 97, 96)", y : 0.811 },
                subtitle : { font : "Lora", size : 4.4, color : "rgb(97, 97, 96)", y : 0.889 }
            },

            // a couple : a wide photo over a red cover
            love : {
                background : "rgb(246, 73, 78)",
                photo : { x : 0, y : 0, width : 1, height : 0.749 },
                title : { font : "Abril Fatface", size : 18, color : "rgb(255, 255, 255)", y : 0.76 },
                subtitle : { font : "Lora", bold : true, size : 5.3, color : "rgb(255, 255, 255)", y : 0.875 }
            },

            // a pet : a round photo, the name written by hand in the bottom corner
            animaux : {
                background : "rgb(203, 196, 42)",
                photo : { x : 0.167, y : 0.164, width : 0.666, square : true, mask : 26 },
                title : { font : "Allura", size : 9.4, color : "rgb(255, 255, 255)", align : "right", x : 0.10, width : 0.827, y : 0.838 },
                subtitle : { font : "Allura", size : 4.7, color : "rgb(139, 135, 29)", align : "right", x : 0.10, width : 0.827, y : 0.889 }
            },

            // a family : the title above the photo, in a light grotesque on deep blue
            family : {
                background : "rgb(52, 62, 130)",
                photo : { x : 0.141, y : 0.184, width : 0.717, height : 0.698 },
                title : { font : "Poiret One", size : 15.7, color : "rgb(255, 255, 255)", y : 0.062 },
                subtitle : { font : "Lora", italic : true, size : 4.4, color : "rgb(255, 255, 255)", align : "right", x : 0.10, width : 0.758, y : 0.888 }
            },

            // a recipe book : the title on top, the dish across the whole width
            cooking : {
                background : "rgb(210, 90, 40)",
                photo : { x : 0, y : 0.209, width : 1, height : 0.582 },
                title : { font : "Abril Fatface", size : 16, color : "rgb(253, 233, 75)", y : 0.066 },
                subtitle : { font : "Lora", bold : true, size : 4.8, color : "rgb(253, 233, 75)", y : 0.145 }
            },

            // a birthday : a round photo of whoever is celebrating, the age in a fat rounded sans
            birthday : {
                background : "rgb(178, 199, 205)",
                coverSubject : true,
                photo : { x : 0.140, y : 0.070, width : 0.718, square : true, mask : 26 },
                title : { font : "Fredoka One", upper : true, size : 15, color : "rgb(212, 154, 132)", y : 0.771 },
                subtitle : { font : "Dancing Script", size : 6.3, color : "rgb(255, 255, 255)", y : 0.861 }
            },

            // a party : a brush title over amber
            party : {
                background : "rgb(253, 180, 47)",
                photo : { x : 0.117, y : 0, width : 0.765, height : 0.699 },
                title : { font : "Knewave", size : 15, color : "rgb(255, 255, 255)", y : 0.731 },
                subtitle : { font : "Lora", bold : true, italic : true, size : 5.2, color : "rgb(255, 255, 255)", y : 0.889 }
            },

            // everyday memories : a polaroid on a warm grey, typewriter caption
            classic : {
                background : "rgb(219, 210, 202)",
                frame : { color : "rgb(255, 255, 255)", x : 0.167, y : 0.164, width : 0.666, height : 0.674 },
                photo : { x : 0.191, y : 0.188, width : 0.618, height : 0.534 },
                title : { font : "Special Elite", size : 6.5, color : "rgb(0, 0, 0)", x : 0.191, width : 0.618, y : 0.742 },
                subtitle : { font : "Special Elite", size : 5.6, color : "rgb(0, 0, 0)", x : 0.191, width : 0.618, y : 0.784 }
            }
        };
    }

    // ------------------------------ countries ------------------------------ //

    /**
     * The trip covers, one design per group of countries (ISO codes of the geocoding, see
     * TitleSuggester.singleCountry) : a plain colour, the photo in the upper part, the title in the
     * font of the design under it and the period, spaced capitals, below. A trip in a country of no
     * group, or in several countries, gets the generic travel design of `categories`.
     */
    static get countries()
    {
        let white = "rgb(255, 255, 255)";

        // the common layout : the photo across the top, the title and the period under it
        let design = (background, title, color, extra) => Object.assign({
            background : background,
            photo : { x : 0.08, y : 0.08, width : 0.82, height : 0.662 },
            title : Object.assign({ size : 17, color : color || white, y : 0.78 }, title),
            subtitle : { font : "Open Sans", upper : true, spaced : true, size : 4.6, color : color || white, y : 0.885 }
        }, extra || {});

        return [
            // Royaume-Uni, Allemagne, Tunisie, Norvège, Monténégro : typewriter on navy
            { countries : ["GB", "DE", "TN", "NO", "ME"], design : design("rgb(49, 62, 95)", { font : "Special Elite" }) },

            // Portugal, Grèce, Luxembourg, Suède, Finlande : a thin geometric on blue
            { countries : ["PT", "GR", "LU", "SE", "FI"], design : design("rgb(0, 130, 208)", { font : "Poiret One", size : 19, y : 0.76 }) },

            // Croatie, Île Maurice, Islande, Cap-Vert, Cuba : a rounded geometric on teal
            { countries : ["HR", "MU", "IS", "CV", "CU"], design : design("rgb(0, 168, 172)", { font : "Righteous", y : 0.77 }) },

            // Italie, Algérie, Canada, Indonésie, Costa Rica : chalk lettering on green
            { countries : ["IT", "DZ", "CA", "ID", "CR"], design : design("rgb(0, 114, 67)", { font : "Fredericka the Great", y : 0.76 }) },

            // Irlande, Vietnam, Sri Lanka, Brésil, Slovénie : a dark green script on lime
            { countries : ["IE", "VN", "LK", "BR", "SI"], design : design("rgb(120, 190, 50)", { font : "Dancing Script", size : 19, y : 0.76 }, "rgb(20, 60, 5)") },

            // Belgique, Égypte, Malte, Sénégal, Émirats : a fat rounded brown on yellow
            { countries : ["BE", "EG", "MT", "SN", "AE"], design : design("rgb(236, 178, 0)", { font : "Fredoka One" }, "rgb(66, 40, 0)") },

            // Thaïlande, Japon, Mexique, Rép. dominicaine, Bulgarie : a swash serif on pink
            { countries : ["TH", "JP", "MX", "DO", "BG"], design : design("rgb(230, 54, 152)", { font : "Berkshire Swash" }) },

            // Espagne, Suisse, États-Unis, Hongrie, Danemark : a heavy slab on red
            { countries : ["ES", "CH", "US", "HU", "DK"], design : design("rgb(230, 0, 26)", { font : "Alfa Slab One" }) },

            // Maroc, Pays-Bas, Rép. tchèque, Chypre, Inde : a portrait photo, a brush title, on orange
            { countries : ["MA", "NL", "CZ", "CY", "IN"], design : design("rgb(238, 116, 0)", { font : "Kaushan Script", size : 18, y : 0.745 }, null, {
                photo : { x : 0.225, y : 0.08, width : 0.55, height : 0.625, mask : "arche.png", ratio : 3409 / 3000, maxWidth : 0.8 },
                subtitle : { font : "Open Sans", upper : true, spaced : true, size : 4.6, color : white, y : 0.865 }
            }) },

            // Turquie, Autriche, Pologne, Albanie, Madagascar : the photo framed in white on burgundy,
            // the period above it and the title below
            { countries : ["TR", "AT", "PL", "AL", "MG"], design : design("rgb(154, 30, 68)", { font : "Abril Fatface", size : 18, y : 0.79 }, null, {
                frame : { color : white, x : 0.10, y : 0.154, width : 0.80, height : 0.595 },
                photo : { x : 0.12, y : 0.172, width : 0.76, height : 0.56  },
                subtitle : { font : "Open Sans", upper : true, spaced : true, size : 4.6, color : white, y : 0.09 }
            }) }
        ];
    }

    /** The design of the country the trip was made in (an ISO code), none otherwise. */
    static countryDesign(country)
    {
        country = String(country || "").toUpperCase();

        let found = country ? CoverDesigns.countries.find((group) => group.countries.indexOf(country) != -1) : null;

        return found ? found.design : null;
    }

    // ------------------------------- cities -------------------------------- //

    /**
     * City breaks : a few days in one of these cities gets the cover of its reference album (the
     * creation id beside each one, on www.flexilivre.com). They all share one layout, read off a
     * 210 x 215 mm square album : the name of the city top left in its own font (`heading`, in the
     * site language) with the dates under it, the photo across the whole width, the album title
     * under the photo in place of the album's "Notre séjour à ..." line, the flag top right and a
     * drawing of the city (a landmark, a bicycle...) in the bottom right corner, over the photo ;
     * the back cover, in the same colour, holds a second photo. The stickers are theme_sticker
     * rows : flags in category 49 (Londres 35), drawings in category 53.
     *
     * `center` and `radius` (km) say where the city is : positions rather than the geocoded names,
     * which come in the site language ("Londres", "London", "Londra"...).
     */
    static get cities()
    {
        // a millimetre of the reference album, as a fraction of its width and of its height
        let mmX = (value) => value / 210, mmY = (value) => value / 215;
        let white = "rgb(255, 255, 255)";
        let lang = (typeof fx.currentLang == "function") ? fx.currentLang() : "fr";

        // every position below is the one of the component in the reference album, millimetres
        // from the top left corner of the front cover. A text component keeps a 5 mm margin inside
        // it (DesignedCover.TEXT_PADDING) : its text starts 5 mm right and down of it.
        //  heading  { font, size, y, upper, dateShift } the name of the city, its component at x 10 ;
        //           dateShift moves the dates right (mm) under a first letter drawn inside its box
        //  title    the width of the "Notre séjour à ..." component, at x 10, y 175
        //  drawing  { id, x, y, width, height } ; the photo ends at 175 mm
        let design = (city, background, heading, title, flag, drawing) => ({
            background : background,
            photo : { x : 0, y : mmY(50), width : 1, height : mmY(125) },
            backPhoto : { x : mmX(40), y : mmY(60), width : mmX(130), height : mmY(90) },
            stickers : [
                { id : flag, right : mmX(15), y : mmY(15), width : mmX(15), height : mmX(10) },
                { id : drawing.id, right : mmX(210 - drawing.x - drawing.width), belowPhoto : mmX(drawing.y + drawing.height - 175), width : mmX(drawing.width), height : mmX(drawing.height) }
            ],
            heading : { text : city.names[lang] || city.names.fr, font : heading.font, size : heading.size, upper : !!heading.upper, color : white, align : "left", x : mmX(15), width : mmX(155), y : mmY(heading.y + 5) },
            subtitle : { font : "Lora", size : 3, color : white, align : "left", fixed : true, x : mmX(15 + (heading.dateShift || 0)), width : mmX(155), y : mmY(40) },   // aligned on the city name
            title : { font : "Lora", size : 5, color : white, align : "left", x : mmX(15), width : mmX(title.width - 5), y : mmY(180) }
        });

        let cities = [
            { name : "Marrakech", album : 4371778, center : [31.6295, -7.9811], radius : 25,
              names : { fr : "Marrakech", en : "Marrakech", es : "Marrakech", de : "Marrakesch", it : "Marrakech" },
              cover : ["rgb(202, 77, 30)", { font : "Aclonica", size : 17, y : 15 }, { width : 140 }, 5375, { id : 8272, x : 145, y : 160, width : 50, height : 40 }] },

            { name : "Madrid", album : 4371743, center : [40.4168, -3.7038], radius : 25,
              names : { fr : "Madrid", en : "Madrid", es : "Madrid", de : "Madrid", it : "Madrid" },
              cover : ["rgb(207, 0, 20)", { font : "Allura", size : 22, y : 10 }, { width : 125 }, 7288, { id : 8923, x : 135, y : 165, width : 65, height : 27 }] },

            { name : "Porto", album : 4371733, center : [41.1579, -8.6291], radius : 20,
              names : { fr : "Porto", en : "Porto", es : "Oporto", de : "Porto", it : "Porto" },
              cover : ["rgb(123, 30, 58)", { font : "Niconne", size : 22, y : 9 }, { width : 140 }, 7326, { id : 8927, x : 150, y : 155, width : 45, height : 45 }] },

            { name : "Prague", album : 4371789, center : [50.0755, 14.4378], radius : 25,
              names : { fr : "Prague", en : "Prague", es : "Praga", de : "Prag", it : "Praga" },
              cover : ["rgb(91, 62, 140)", { font : "Mystery Quest", size : 17, y : 14, upper : true }, { width : 140 }, 7457, { id : 8926, x : 150, y : 155, width : 45, height : 45 }] },

            { name : "Barcelone", album : 4371465, center : [41.3874, 2.1686], radius : 25,
              names : { fr : "Barcelone", en : "Barcelona", es : "Barcelona", de : "Barcelona", it : "Barcellona" },
              cover : ["rgb(244, 196, 48)", { font : "Sigmar One", size : 21, y : 10, dateShift : 1 }, { width : 145 }, 7288, { id : 8924, x : 160, y : 140, width : 40, height : 65 }] },

            { name : "Bruxelles", album : 4371759, center : [50.8503, 4.3517], radius : 20,
              names : { fr : "Bruxelles", en : "Brussels", es : "Bruselas", de : "Brüssel", it : "Bruxelles" },
              cover : ["rgb(90, 58, 41)", { font : "Bangers", size : 22, y : 9 }, { width : 140 }, 5367, { id : 8925, x : 150, y : 165, width : 45, height : 35 }] },

            { name : "Rome", album : 4371553, center : [41.9028, 12.4964], radius : 30,
              names : { fr : "Rome", en : "Rome", es : "Roma", de : "Rom", it : "Roma" },
              cover : ["rgb(201, 128, 59)", { font : "Abril Fatface", size : 21, y : 9 }, { width : 140 }, 7402, { id : 8239, x : 155, y : 160, width : 40, height : 40 }] },

            { name : "Lisbonne", album : 4371644, center : [38.7223, -9.1393], radius : 25,
              names : { fr : "Lisbonne", en : "Lisbon", es : "Lisboa", de : "Lissabon", it : "Lisbona" },
              cover : ["rgb(30, 90, 168)", { font : "Kaushan Script", size : 21, y : 9, dateShift : 1 }, { width : 145 }, 7326, { id : 8241, x : 160, y : 150, width : 30, height : 50 }] },

            // the reference album is painted rgb(30, 90, 168) like Lisbonne : the blue asked for
            // Londres is kept instead
            { name : "Londres", album : 4371681, center : [51.5074, -0.1278], radius : 35,
              names : { fr : "Londres", en : "London", es : "Londres", de : "London", it : "Londra" },
              cover : ["rgb(48, 43, 136)", { font : "Lora", size : 21, y : 8, dateShift : 1 }, { width : 155 }, 1726, { id : 8280, x : 170, y : 135, width : 15, height : 65 }] },

            { name : "Amsterdam", album : 4371701, center : [52.3676, 4.9041], radius : 20,
              names : { fr : "Amsterdam", en : "Amsterdam", es : "Ámsterdam", de : "Amsterdam", it : "Amsterdam" },
              cover : ["rgb(255, 111, 0)", { font : "Chewy", size : 21, y : 10 }, { width : 130 }, 7342, { id : 8230, x : 140, y : 155, width : 55, height : 45 }] }
        ];

        return cities.map((city) => Object.assign(city, { design : design(city, ...city.cover) }));
    }

    /** A city break lasts this many days at most ("quelques jours") ; longer, it is a journey. */
    static get CITY_MAX_DAYS()
    {
        return 8;
    }

    /** Located photos needed, and the share of them that must be in the city. */
    static get CITY_MIN_LOCATED()
    {
        return 3;
    }

    static get CITY_SHARE()
    {
        return 2 / 3;
    }

    /**
     * The city of `cities` the photos were taken in, when they make a city break : CITY_MAX_DAYS at
     * most between the first and the last capture, and CITY_SHARE of the located photos within the
     * radius of that city (a day trip out of town leaves it the city break it is). Null otherwise.
     */
    static city(photos)
    {
        let list = (photos || []).filter((photo) => photo && !photo.deleted);

        if (CoverDesigns.spanDays(list) > CoverDesigns.CITY_MAX_DAYS)
            return null;

        let located = list
            .map((photo) => ({ lat : Number(photo.latitude), lng : Number(photo.longitude) }))
            .filter((point) => point.lat && point.lng && !isNaN(point.lat) && !isNaN(point.lng));

        if (located.length < CoverDesigns.CITY_MIN_LOCATED)
            return null;

        let cities = CoverDesigns.cities;

        for (let i = 0; i < cities.length; i++)
        {
            let city = cities[i];
            let inside = located.filter((point) => curator.MomentClustering.distance(point.lat, point.lng, city.center[0], city.center[1]) <= city.radius * 1000).length;

            if (inside >= located.length * CoverDesigns.CITY_SHARE)
                return city;
        }

        return null;
    }

    /** The city named by ?cover-city= (one of its `names`, case and accents ignored), null otherwise. */
    static forcedCity()
    {
        let plain = (text) => String(text || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase();
        let asked = (typeof Utils != "undefined" && Utils.getURLParam) ? plain(Utils.getURLParam("cover-city")) : "";

        return asked ? (CoverDesigns.cities.find((city) => Object.keys(city.names).some((lang) => plain(city.names[lang]) == asked)) || null) : null;
    }

    static cityDesign(photos)
    {
        let city = CoverDesigns.city(photos);

        return city ? city.design : null;
    }

    /** Calendar days between the first and the last capture, both counted ; 0 when no date is known. */
    static spanDays(photos)
    {
        let times = photos.map((photo) => curator.MomentClustering.time(photo)).filter((time) => time > 0);

        if (!times.length)
            return 0;

        let first = new Date(Math.min.apply(null, times) * 1000), last = new Date(Math.max.apply(null, times) * 1000);
        let dayStart = new Date(first.getFullYear(), first.getMonth(), first.getDate());
        let dayEnd = new Date(last.getFullYear(), last.getMonth(), last.getDate());

        return Math.round((dayEnd - dayStart) / 86400000) + 1;
    }
});
